/**
 * Copyright © Ircam Amplify 2024-2026
 */

import { logger } from '@/config/logger'
import { CheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useCallback, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { API } from '../api'
import CheckoutForm from '../components/CheckoutForm'
import CheckoutSkeleton from '../components/CheckoutSkeleton'
import FullScreenLayout from '../components/FullScreenLayout'
import { getUserState, useSelector } from '../store'
import { BillingInformationState } from './BillingInformationState'

export const Checkout = () => {
  // Load the Stripe promise
  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_API_KEY, {
    betas: ['custom_checkout_beta_6'],
    locale: 'en',
  })

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const location = useLocation()
  const {
    tier,
    subscriptionFrequency,
    priceIdList,
    isEuroCurrency,
    numberOfCreditsForSubscription,
    offPackageCreditCost,
    planName,
  } = (location.state as BillingInformationState) || {}
  const { userInfo } = useSelector(getUserState)

  // Redirect if any required billing information is missing
  if (!tier || !priceIdList || isEuroCurrency === undefined) {
    return <Navigate to="/tierselection" />
  }

  const fetchClientSecret = useCallback(async () => {
    setIsLoading(true)
    // Create a Checkout Session
    return API(await fetchAuthSession())
      .post('/stripe/create-checkout-session', {
        priceIdList: priceIdList.split(','),
        tier,
        subscriptionFrequency,
        isEuroCurrency,
        user_email: userInfo?.value.user_email,
      })
      .then((res) => {
        return res.data.clientSecret
      })
      .catch((error) => {
        setIsLoading(false)
        logger.error('Error:', error.response.data.message)
        setErrorMessage(
          'Error while creating checkout session: ' +
            error.response.data.message
        )
        return
      })
  }, [])

  const appearance = {
    theme: 'flat',
    variables: {
      fontFamily: ' "Barlow", sans-serif',
      fontLineHeight: '1.5',
      borderRadius: '10px',
      colorBackground: '#F6F8FA',
      accessibleColorOnColorPrimary: '#262626',
    },
    rules: {
      '.Block': {
        backgroundColor: 'var(--colorBackground)',
        boxShadow: 'none',
        padding: '12px',
      },
      '.Input': {
        padding: '12px',
      },
      '.Input:disabled, .Input--invalid:disabled': {
        color: 'lightgray',
      },
      '.Tab': {
        padding: '10px 12px 8px 12px',
        border: 'none',
      },
      '.Tab:hover': {
        border: 'none',
        boxShadow:
          '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
      },
      '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
        border: 'none',
        backgroundColor: '#fff',
        boxShadow:
          '0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
      },
      '.Label': {
        fontWeight: '500',
      },
    },
  }

  const options = {
    fetchClientSecret,
    elementsOptions: {
      appearance,
    },
  }

  return (
    <FullScreenLayout title="Checkout" subtitle="">
      <div id="checkout">
        <CheckoutProvider stripe={stripePromise} options={options}>
          <CheckoutForm
            tier={tier}
            subscriptionFrequency={subscriptionFrequency}
            planName={planName}
            offPackageCreditCost={offPackageCreditCost}
            numberOfCreditsForSubscription={numberOfCreditsForSubscription}
            setIsloading={setIsLoading}
          />
        </CheckoutProvider>
        {isLoading && <CheckoutSkeleton />}
      </div>
      {errorMessage && <div>{errorMessage}</div>}
    </FullScreenLayout>
  )
}
